import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { onEvent } from "../lib/realtime";
import { useI18n } from "../lib/i18n.jsx";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusPill, Empty } from "../components/Bits";
import { Button } from "../components/ui/button";
import { FileDown, Pencil, Trash2 } from "lucide-react";
import ReportEditDialog, { deleteReport } from "../components/ReportEditDialog";
import TimeAllocation from "../components/TimeAllocation";
import PdfLang from "../components/PdfLang";

const hm = (iso) => (iso ? iso.slice(11, 16) : "—");
const OWN_EDITABLE = ["draft", "submitted", "rejected"];

export default function WorkerReports() {
  const { t, tn, lang } = useI18n();
  const { can } = useAuth();
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState(null);
  const [pdfLang, setPdfLang] = useState(lang || "en");
  useEffect(() => { setPdfLang(lang || "en"); }, [lang]);

  const load = () => api.get("/reports").then((r) => setReports(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => onEvent((ev) => { if (ev.type.startsWith("report.")) load(); }), []);

  const view = async (id) => {
    const { data } = await api.get(`/reports/${id}`);
    setOpen(data);
  };
  const editable = open && can("reports.edit") && OWN_EDITABLE.includes(open.status);

  return (
    <div className="max-w-md mx-auto">
      <PageHeader title={t("my_reports")} action={
        <Button variant="outline" size="sm" className="gap-1 h-10" data-testid="worker-pdf-period" onClick={async () => {
          const d = new Date(); const f = new Date(); f.setDate(d.getDate() - 6); const from = f.toISOString().slice(0, 10);
          try { const { data } = await api.get(`/pdf/report?date_from=${from}&date_to=${d.toISOString().slice(0, 10)}&lang=${pdfLang}`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); }
          catch { toast.error(t("error")); }
        }}><FileDown size={15} /> {t("pdf_period")}</Button>} />
      {reports.length === 0 ? <Empty /> : (
        <div className="space-y-2">
          {reports.map((r) => (
            <button key={r.id} data-testid={`report-${r.id}`} onClick={() => view(r.id)}
              className="w-full tactical-card p-4 text-left hover:border-[var(--amber)] transition-colors">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{r.date}</span>
                <StatusPill status={r.status} />
              </div>
              <div className="text-sm text-[var(--text-3)] mt-1">{r.items_count} {t("work").toLowerCase()}{r.created_by_name && ` · ${t("created_by")}: ${r.created_by_name}`}</div>
            </button>
          ))}
        </div>
      )}

      {open && createPortal(
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={() => setOpen(null)}>
          <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl p-4 sm:p-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[85vh] overflow-y-auto overflow-x-hidden break-words" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-bold text-lg">{open.date}</h3>
              <StatusPill status={open.status} />
            </div>
            {open.shift && <div className="text-sm text-[var(--text-3)] font-mono-num mb-2" data-testid="worker-report-shift">{t("shift")}: {hm(open.shift.start_time)}–{hm(open.shift.end_time)} ({open.shift.break_minutes || 0}′) = <b>{open.shift.hours}h</b></div>}
            {open.created_by_name && <div className="text-xs text-[var(--text-3)] mb-2">{t("created_by")}: {open.created_by_name}{open.created_for_reason && ` — ${open.created_for_reason}`}</div>}
            <div className="space-y-2">
              {open.items.map((it) => (
                <div key={it.id} className="border-b border-[var(--border)] py-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    {it.item_number && <span className="text-xs text-[var(--text-3)] font-mono-num shrink-0">{it.item_number}</span>}
                    <span className="flex-1 min-w-0 break-words">{tn(it.work_name) || "…"}</span>
                    <span className="font-mono-num font-semibold shrink-0 whitespace-nowrap">{it.quantity} {tn(it.unit_name)}</span>
                  </div>
                  <div className="text-xs text-[var(--text-3)] font-mono-num">
                    {t("actual_min_short")}: {it.actual_minutes ?? "—"}
                    {it.norm_minutes !== undefined && ` · ${t("norm_min_short")}: ${it.norm_minutes ? Math.round(it.norm_minutes) : "—"}`}
                    {it.activity_type === "NON_PRODUCTIVE"
                      ? ` · ${t("NON_PRODUCTIVE")}${it.norm_usage_pct != null ? ` · ${it.norm_usage_pct}% ${t("norm_usage")} (${it.deviation_minutes > 0 ? "+" : ""}${it.deviation_minutes}′)` : ""}`
                      : (it.efficiency !== undefined ? ` · ${t("efficiency")}: ${it.efficiency != null ? it.efficiency + "%" : t("no_norm")}` : "")}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3"><TimeAllocation alloc={open.time_allocation} testid="worker-time-allocation" /></div>
            {open.foreman_comment && <p className="text-sm mt-3 text-[var(--red)]">⚑ {open.foreman_comment}</p>}
            {editable && (
              <div className="flex gap-2 mt-3">
                <Button variant="outline" onClick={() => setEdit(open)} className="flex-1 gap-1" data-testid="worker-edit-report"><Pencil size={15} /> {t("edit")}</Button>
                <Button variant="outline" onClick={async () => { if (await deleteReport(open, false, t)) { setOpen(null); load(); } }} className="flex-1 gap-1 text-[var(--red)]" data-testid="worker-delete-report"><Trash2 size={15} /> {t("delete")}</Button>
              </div>
            )}
            <PdfLang value={pdfLang} onChange={setPdfLang} className="mt-3" testid="worker-pdf-lang" />
            <Button data-testid="report-pdf-btn" onClick={() => window.open(`${fileUrl("x").replace("/files/x", `/reports/${open.id}/pdf`)}&lang=${pdfLang}`, "_blank")}
              className="w-full mt-3 bg-[var(--sidebar)] text-white gap-2">
              <FileDown size={16} /> {t("export_pdf")}
            </Button>
          </div>
        </div>, document.body)}
      {edit && <ReportEditDialog report={edit} needReason={false} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); view(edit.id); load(); }} />}
    </div>
  );
}
